import { useQuery } from "@tanstack/react-query";

import { getDiagnosticScopeItems, type DiagnosticScopeItem } from "@/api/diagnostic";
import { diagnosticKeys } from "@/hooks/useDiagnostic";

/** A base que o tile contou: a versão aprovada, ou `null` para a curadoria salva. */
export interface ScopeItemsBasis {
  approvalVersion: number | null;
}

/**
 * A chave fica debaixo do prefixo do snapshot do Diagnóstico, como a da árvore
 * da estrutura organizacional: quem grava uma decisão invalida o snapshot para
 * o tile mudar, e o TanStack casa por prefixo, então a lista é relida junto.
 */
export const diagnosticScopeItemsKeys = {
  active: (projectId: string, basis: ScopeItemsBasis | undefined) =>
    [...diagnosticKeys.snapshot(projectId), "scope-items", basis?.approvalVersion ?? null] as const,
};

/**
 * Os scope items ativos e os totais do tile, para o detalhe do Ato 1 (#1344, S5).
 *
 * Lê o `GET /diagnostic/scope-items` (#1506), que conta com a mesma função do
 * tile. Sem `basis` (o tile não tem número), não pede nada.
 */
export function useDiagnosticScopeItems(
  projectId: string | undefined,
  basis: ScopeItemsBasis | undefined,
) {
  return useQuery({
    queryKey: diagnosticScopeItemsKeys.active(projectId ?? "", basis),
    queryFn: () => getDiagnosticScopeItems(projectId!, basis!.approvalVersion),
    enabled: !!projectId && !!basis,
    staleTime: 30_000,
  });
}

/** Um scope item do escopo, com o destino pelo qual a lista o trouxe. */
export interface InScopeItem extends DiagnosticScopeItem {
  destination: "activate" | "decision";
}

/**
 * Os scope items do escopo, ativar e decisão, para os detalhes do Ato 2
 * (19/10/2026). É a mesma população dos cards de aderência e de destino: o escopo
 * aprovado, na versão que o tile "Scope items ativos" contou.
 *
 * Lê a foto da aprovação pelo `GET /diagnostic/scope-items`, e não a curadoria
 * corrente: com Calibragem publicada, o tile conta a aprovação do carimbo dela, e
 * a tabela por processo vem da mesma publicação. Antes, a lista lia
 * `/scope-activation/items` e mostrava a aprovação nova ao lado da tabela antiga
 * (visto no teste da release em 21/10/2026).
 */
export function useDiagnosticInScopeItems(
  projectId: string | undefined,
  basis: ScopeItemsBasis | undefined,
) {
  return useQuery({
    queryKey: [
      ...diagnosticKeys.snapshot(projectId ?? ""),
      "scope-items-in-scope",
      basis?.approvalVersion ?? null,
    ] as const,
    queryFn: async (): Promise<InScopeItem[]> => {
      const [activate, decision] = await Promise.all([
        getDiagnosticScopeItems(projectId!, basis!.approvalVersion, "activate"),
        getDiagnosticScopeItems(projectId!, basis!.approvalVersion, "decision"),
      ]);
      return [
        ...activate.items.map((row) => ({ ...row, destination: "activate" as const })),
        ...decision.items.map((row) => ({ ...row, destination: "decision" as const })),
      ];
    },
    enabled: !!projectId && !!basis,
    staleTime: 30_000,
  });
}
